import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import Button from "./Button";

/**
 * Reusable confirmation modal for delete/destructive actions
 * @param {boolean} open - Controls modal visibility
 * @param {function} onClose - Handler for closing the modal
 * @param {function} onConfirm - Handler for confirming the action
 * @param {string} title - Modal title (defaults to translated value)
 * @param {string} message - Confirmation message/description (defaults to translated value)
 * @param {string} confirmText - Text for confirm button (defaults to translated value)
 * @param {string} cancelText - Text for cancel button (defaults to translated value)
 * @param {string} confirmColor - Color for confirm button (default: "error")
 * @param {boolean} loading - Shows loading state on confirm button
 */
const ConfirmationModal = ({
  open,
  onClose,
  onConfirm,
  title,
  message,
  confirmText,
  cancelText,
  confirmColor = "error",
  loading = false,
}) => {
  const { t } = useTranslation("common");
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
      data-testid="confirmation-modal"
    >
      <DialogTitle>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            py: 2,
          }}
        >
          <Typography variant="h2">
            {title || t("confirmationModal.title")}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Typography component="p" textAlign="center">
          {message || t("confirmationModal.message")}
        </Typography>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          onClick={onClose}
          variant="secondary"
          disabled={loading}
          data-testid="confirmation-cancel-button"
        >
          {cancelText || t("confirmationModal.cancel")}
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          color={confirmColor}
          disabled={loading}
          sx={{ ml: 2 }}
          data-testid="confirmation-confirm-button"
        >
          {loading
            ? t("confirmationModal.processing")
            : confirmText || t("confirmationModal.confirm")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

ConfirmationModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  title: PropTypes.string,
  message: PropTypes.string,
  confirmText: PropTypes.string,
  cancelText: PropTypes.string,
  confirmColor: PropTypes.string,
  loading: PropTypes.bool,
};

export default ConfirmationModal;
